import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PiTrashBold, PiUserPlusBold } from 'react-icons/pi';
import { Button, Input, Text } from '@v2/design-system';
import ConfirmModal from '@v2/components/ui/ConfirmModal';
import V2Modal from '@v2/components/ui/V2Modal';
import api from '@v2/lib/api';
import { useI18n } from '@v2/providers/I18nProvider';
import { cn } from '@v2/utils/cn';
import { invalidateDealCommissionQueries } from './invalidateDealCommissionQueries';

type CascadeEntry = {
    commission_role: string;
    user: { id: number; name: string } | null;
    rate_pp: number;
    total_amount: number;
    open_slices: number;
    locked_slices: number;
};

type CascadeState = {
    frozen: boolean;
    entries: CascadeEntry[];
};

type UserOption = {
    id: number;
    name: string;
    email?: string | null;
};

type Props = {
    open: boolean;
    onClose: () => void;
    dealId: number | string;
};

type PendingForceAction = 'save' | 'zero' | 'remove' | null;

export default function DealLegacyCascadeModal({ open, onClose, dealId }: Props) {
    const { t } = useI18n();
    const queryClient = useQueryClient();
    const [role, setRole] = useState<'gerente' | 'diretor'>('gerente');
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [selected, setSelected] = useState<UserOption | null>(null);
    const [ratePp, setRatePp] = useState('1');
    const [reason, setReason] = useState('');
    const [forceConfirm, setForceConfirm] = useState<PendingForceAction>(null);

    useEffect(() => {
        const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
        return () => window.clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        if (!open) {
            setRole('gerente');
            setSearch('');
            setDebouncedSearch('');
            setSelected(null);
            setRatePp('1');
            setReason('');
            setForceConfirm(null);
        }
    }, [open]);

    const cascadeQuery = useQuery({
        queryKey: ['commission-legacy-cascade', dealId],
        queryFn: async () => {
            const { data } = await api.get<{ data: CascadeState }>(
                `/commissioning/deals/${dealId}/legacy-cascade`,
            );
            return data.data;
        },
        enabled: open && Boolean(dealId),
        staleTime: 15_000,
    });

    const currentEntry = useMemo(
        () => (cascadeQuery.data?.entries ?? []).find((e) => e.commission_role === role) ?? null,
        [cascadeQuery.data, role],
    );

    const lockedCount = currentEntry?.locked_slices ?? 0;

    useEffect(() => {
        if (!currentEntry) {
            return;
        }
        if (currentEntry.user) {
            setSelected({ id: currentEntry.user.id, name: currentEntry.user.name });
            setSearch(currentEntry.user.name);
        }
        setRatePp(String(currentEntry.rate_pp));
    }, [currentEntry?.commission_role, currentEntry?.user?.id, currentEntry?.rate_pp]);

    const usersQuery = useQuery({
        queryKey: ['users-search-cascade', debouncedSearch],
        queryFn: async () => {
            const { data } = await api.get<{ data: UserOption[] }>('/users', {
                params: {
                    search: debouncedSearch || undefined,
                    per_page: 20,
                },
            });
            return data.data ?? [];
        },
        enabled: open && debouncedSearch.length >= 2,
        staleTime: 30_000,
    });

    const invalidate = async () => {
        await invalidateDealCommissionQueries(queryClient, dealId);
    };

    const saveMutation = useMutation({
        mutationFn: async (forceLocked: boolean) => {
            if (!selected?.id) {
                throw new Error('missing_beneficiary');
            }
            const { data } = await api.put(`/commissioning/deals/${dealId}/legacy-cascade`, {
                role,
                beneficiary_user_id: selected.id,
                rate_pp: Number(String(ratePp).replace(',', '.')),
                reason: reason.trim() || null,
                force_locked: forceLocked,
            });
            return data as {
                message?: string;
                data?: { updated?: number; created?: number; skipped_locked?: number; updated_locked?: number };
            };
        },
        onSuccess: async (data) => {
            setForceConfirm(null);
            const msg = data?.message ?? t('v2.ploomes_deals.legacy_cascade.toast_ok');
            const touched =
                Number(data?.data?.updated ?? 0)
                + Number(data?.data?.created ?? 0)
                + Number(data?.data?.updated_locked ?? 0);
            if (touched > 0) {
                toast.success(msg);
            } else {
                toast.error(msg);
            }
            await invalidate();
        },
        onError: (err: unknown) => {
            const errors = (err as { response?: { data?: { errors?: Record<string, string[]> } } })
                ?.response?.data?.errors;
            const msg =
                errors?.deal?.[0]
                ?? errors?.beneficiary_user_id?.[0]
                ?? errors?.rate_pp?.[0]
                ?? errors?.reason?.[0]
                ?? (err as { response?: { data?: { message?: string } } })?.response?.data?.message
                ?? t('v2.ploomes_deals.legacy_cascade.toast_error');
            toast.error(msg);
        },
    });

    const zeroMutation = useMutation({
        mutationFn: async (forceLocked: boolean) => {
            const { data } = await api.post(`/commissioning/deals/${dealId}/legacy-cascade/zero`, {
                role,
                reason: reason.trim() || null,
                force_locked: forceLocked,
            });
            return data;
        },
        onSuccess: async () => {
            setForceConfirm(null);
            toast.success(t('v2.ploomes_deals.legacy_cascade.toast_zeroed'));
            await invalidate();
        },
        onError: () => toast.error(t('v2.ploomes_deals.legacy_cascade.toast_error')),
    });

    const removeMutation = useMutation({
        mutationFn: async (forceLocked: boolean) => {
            const { data } = await api.delete(`/commissioning/deals/${dealId}/legacy-cascade`, {
                data: { role, reason: reason.trim() || null, force_locked: forceLocked },
            });
            return data;
        },
        onSuccess: async () => {
            setForceConfirm(null);
            toast.success(t('v2.ploomes_deals.legacy_cascade.toast_removed'));
            await invalidate();
            setSelected(null);
            setSearch('');
        },
        onError: () => toast.error(t('v2.ploomes_deals.legacy_cascade.toast_error')),
    });

    const busy = saveMutation.isPending || zeroMutation.isPending || removeMutation.isPending;
    const hasUser = Boolean(currentEntry?.user);

    const requestAction = (action: PendingForceAction) => {
        if (!action) return;
        if (lockedCount > 0) {
            setForceConfirm(action);
            return;
        }
        if (action === 'save') saveMutation.mutate(false);
        if (action === 'zero') zeroMutation.mutate(false);
        if (action === 'remove') removeMutation.mutate(false);
    };

    const confirmForce = () => {
        if (forceConfirm === 'save') saveMutation.mutate(true);
        if (forceConfirm === 'zero') zeroMutation.mutate(true);
        if (forceConfirm === 'remove') removeMutation.mutate(true);
    };

    return (
        <>
            <V2Modal open={open} onClose={onClose} title={t('v2.ploomes_deals.legacy_cascade.title')} size="md">
                <div className="space-y-4">
                    <Text className="text-sm text-gray-600 dark:text-zinc-400">
                        {t('v2.ploomes_deals.legacy_cascade.description')}
                    </Text>

                    <div className="flex gap-2">
                        {(['gerente', 'diretor'] as const).map((r) => (
                            <button
                                key={r}
                                type="button"
                                className={cn(
                                    'rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors',
                                    role === r
                                        ? 'border-sky-400 bg-sky-50 text-sky-800 dark:border-sky-600 dark:bg-sky-950/40 dark:text-sky-200'
                                        : 'border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-zinc-700 dark:text-zinc-300',
                                )}
                                onClick={() => setRole(r)}
                            >
                                {t(`v2.ploomes_deals.legacy_cascade.role_${r}`)}
                            </button>
                        ))}
                    </div>

                    {currentEntry && (
                        <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-950 dark:border-sky-800/50 dark:bg-sky-950/30 dark:text-sky-100">
                            {currentEntry.user
                                ? t('v2.ploomes_deals.legacy_cascade.current', {
                                      name: currentEntry.user.name,
                                      percent: String(currentEntry.rate_pp),
                                      amount: currentEntry.total_amount.toLocaleString('pt-BR', {
                                          style: 'currency',
                                          currency: 'BRL',
                                      }),
                                  })
                                : t('v2.ploomes_deals.legacy_cascade.current_empty')}
                            {lockedCount > 0 && (
                                <span className="mt-1 block opacity-80">
                                    {t('v2.ploomes_deals.legacy_cascade.locked_hint', {
                                        count: String(lockedCount),
                                    })}
                                </span>
                            )}
                        </div>
                    )}

                    <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-gray-700 dark:text-zinc-300">
                            {t('v2.ploomes_deals.legacy_cascade.beneficiary')}
                        </label>
                        <Input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={t('v2.ploomes_deals.legacy_cascade.search_placeholder')}
                        />
                        {selected && (
                            <div className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-2.5 py-2 text-xs dark:border-zinc-700 dark:bg-zinc-800/60">
                                <span className="font-medium text-gray-900 dark:text-zinc-100">{selected.name}</span>
                                <button
                                    type="button"
                                    className="text-gray-500 hover:text-gray-800 dark:hover:text-zinc-200"
                                    onClick={() => setSelected(null)}
                                >
                                    {t('v2.ploomes_deals.legacy_cascade.clear_selection')}
                                </button>
                            </div>
                        )}
                        {debouncedSearch.length >= 2 && (
                            <ul className="max-h-40 overflow-auto rounded-md border border-gray-200 dark:border-zinc-700">
                                {(usersQuery.data ?? []).map((user) => (
                                    <li key={user.id}>
                                        <button
                                            type="button"
                                            className={cn(
                                                'flex w-full flex-col items-start px-2.5 py-2 text-left text-xs hover:bg-gray-50 dark:hover:bg-zinc-800',
                                                selected?.id === user.id && 'bg-sky-50 dark:bg-sky-950/40',
                                            )}
                                            onClick={() => {
                                                setSelected(user);
                                                setSearch(user.name);
                                            }}
                                        >
                                            <span className="font-medium text-gray-900 dark:text-zinc-100">{user.name}</span>
                                            {user.email && (
                                                <span className="text-[10px] text-gray-500">{user.email}</span>
                                            )}
                                        </button>
                                    </li>
                                ))}
                                {usersQuery.isFetched && (usersQuery.data ?? []).length === 0 && (
                                    <li className="px-2.5 py-2 text-xs text-gray-500">
                                        {t('v2.ploomes_deals.legacy_cascade.no_results')}
                                    </li>
                                )}
                            </ul>
                        )}
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-gray-700 dark:text-zinc-300">
                            {t('v2.ploomes_deals.legacy_cascade.rate_pp')}
                        </label>
                        <Input
                            value={ratePp}
                            onChange={(e) => setRatePp(e.target.value)}
                            inputMode="decimal"
                        />
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-gray-700 dark:text-zinc-300">
                            {t('v2.ploomes_deals.legacy_cascade.reason')}
                        </label>
                        <Input
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder={t('v2.ploomes_deals.legacy_cascade.reason_placeholder')}
                        />
                    </div>

                    <Text className="text-[11px] text-gray-500 dark:text-zinc-400">
                        {t('v2.ploomes_deals.legacy_cascade.hint')}
                    </Text>

                    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-3 dark:border-zinc-800">
                        <div className="flex flex-wrap gap-2">
                            {hasUser && (
                                <>
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        disabled={busy}
                                        isLoading={zeroMutation.isPending}
                                        onClick={() => requestAction('zero')}
                                    >
                                        {t('v2.ploomes_deals.legacy_cascade.zero')}
                                    </Button>
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        className="border-red-300 text-red-700 dark:border-red-800 dark:text-red-300"
                                        disabled={busy}
                                        isLoading={removeMutation.isPending}
                                        onClick={() => requestAction('remove')}
                                    >
                                        <PiTrashBold className="mr-1 h-3.5 w-3.5" />
                                        {t('v2.ploomes_deals.legacy_cascade.remove')}
                                    </Button>
                                </>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <Button type="button" size="sm" variant="outline" disabled={busy} onClick={onClose}>
                                {t('v2.ploomes_deals.legacy_cascade.cancel')}
                            </Button>
                            <Button
                                type="button"
                                size="sm"
                                isLoading={saveMutation.isPending}
                                disabled={busy || !selected?.id}
                                onClick={() => requestAction('save')}
                            >
                                <PiUserPlusBold className="mr-1 h-3.5 w-3.5" />
                                {t('v2.ploomes_deals.legacy_cascade.save')}
                            </Button>
                        </div>
                    </div>
                </div>
            </V2Modal>

            <ConfirmModal
                open={forceConfirm !== null}
                onClose={() => setForceConfirm(null)}
                onConfirm={confirmForce}
                loading={busy}
                title={t('v2.ploomes_deals.legacy_cascade.force_paid_title')}
                description={t('v2.ploomes_deals.legacy_cascade.force_paid_body', {
                    count: String(lockedCount),
                })}
                confirmLabel={t('v2.ploomes_deals.legacy_cascade.force_paid_confirm')}
                cancelLabel={t('v2.ploomes_deals.legacy_cascade.cancel')}
            />
        </>
    );
}
